import React, { useState, useRef } from 'react';
import { parseBackupZip } from '../utils/backup.js';

export default function RestoreBackupModal({ onRestore, onClose }) {
    const [fileName, setFileName] = useState('');
    const [parsed, setParsed] = useState(null); // { manifest, zip }
    const [selectedFolderIds, setSelectedFolderIds] = useState(new Set());
    const [error, setError] = useState('');
    const [isParsing, setIsParsing] = useState(false);
    const [isRestoring, setIsRestoring] = useState(false);
    const fileInputRef = useRef(null);

    const handleFile = async (file) => {
        setError('');
        setParsed(null);
        setFileName(file.name);
        setIsParsing(true);
        try {
            const result = await parseBackupZip(file);
            setParsed(result);
            setSelectedFolderIds(new Set((result.manifest.folders || []).map((f) => f.id)));
            if (!result.manifest.folders?.length) setError('This backup contains no folders.');
        } catch (err) {
            setError(err.message || 'Failed to read backup file.');
        } finally {
            setIsParsing(false);
        }
    };

    const toggleFolder = (id) => {
        setSelectedFolderIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const allSelected = parsed && selectedFolderIds.size === (parsed.manifest.folders || []).length;
    const toggleSelectAll = () => {
        if (!parsed) return;
        setSelectedFolderIds(allSelected ? new Set() : new Set(parsed.manifest.folders.map((f) => f.id)));
    };

    const selectedCardCount = parsed
        ? parsed.manifest.folders
            .filter((f) => selectedFolderIds.has(f.id))
            .reduce((sum, f) => sum + (f.cards?.length || 0), 0)
        : 0;

    const handleRestore = async () => {
        if (!parsed || selectedFolderIds.size === 0) return;
        setIsRestoring(true);
        setError('');
        try {
            await onRestore({ manifest: parsed.manifest, zip: parsed.zip, selectedFolderIds });
            onClose();
        } catch (err) {
            setError(err.message || 'Restore failed.');
        } finally {
            setIsRestoring(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="w-full max-w-xl rounded-2xl border border-white/10 bg-slate-900/98 shadow-2xl flex flex-col overflow-hidden" style={{ maxHeight: '90vh' }}>
                <div className="flex items-center justify-between px-5 py-4 border-b border-white/8 shrink-0">
                    <div>
                        <h2 className="text-base font-semibold text-white">Restore from Backup</h2>
                        <p className="text-xs text-slate-400 mt-0.5">From a FlashFonic backup .zip file</p>
                    </div>
                    <button onClick={onClose} className="rounded-full p-1.5 text-slate-400 hover:text-white hover:bg-white/10 transition">
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                    <p className="text-xs text-slate-400">
                        Restoring always creates new folders — it will not merge into or overwrite existing decks.
                        Saved exam history in the backup is informational only and can't be restored.
                    </p>

                    <div
                        onClick={() => fileInputRef.current?.click()}
                        className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-white/15 bg-white/3 py-10 cursor-pointer hover:border-teal-500/40 hover:bg-teal-500/5 transition"
                    >
                        <svg className="h-10 w-10 text-slate-500 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <p className="text-sm text-slate-300 font-medium">{isParsing ? 'Reading backup…' : (fileName || 'Click to select a backup .zip file')}</p>
                        <p className="text-xs text-slate-500 mt-1">flashfonic-backup-*.zip</p>
                    </div>
                    <input ref={fileInputRef} type="file" accept=".zip" className="hidden"
                        onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])} />

                    {error && <p className="text-sm text-rose-300 bg-rose-500/10 rounded-xl px-3 py-2">{error}</p>}

                    {parsed && parsed.manifest.folders?.length > 0 && (
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <p className="text-xs font-semibold text-slate-400">
                                    {parsed.manifest.folders.length} folder{parsed.manifest.folders.length === 1 ? '' : 's'} found — exported {new Date(parsed.manifest.exportedAt).toLocaleDateString()}
                                </p>
                                <button onClick={toggleSelectAll} className="text-xs text-teal-400 hover:text-teal-300 transition">
                                    {allSelected ? 'Deselect All' : 'Select All'}
                                </button>
                            </div>
                            <div className="rounded-xl border border-white/8 bg-slate-950/40 overflow-hidden max-h-52 overflow-y-auto">
                                {parsed.manifest.folders.map((f) => (
                                    <label key={f.id} className="flex items-center gap-3 px-3 py-2.5 border-b border-white/5 last:border-0 cursor-pointer hover:bg-white/5">
                                        <input
                                            type="checkbox"
                                            checked={selectedFolderIds.has(f.id)}
                                            onChange={() => toggleFolder(f.id)}
                                            className="h-4 w-4 rounded border-slate-600 bg-slate-950/60 text-teal-500 focus:ring-teal-500/40"
                                        />
                                        <span className="flex-1 min-w-0 text-sm text-slate-200 truncate">{f.name}</span>
                                        <span className="text-xs text-slate-500 shrink-0">{f.cards?.length || 0} cards</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="shrink-0 flex items-center justify-end gap-3 px-5 py-4 border-t border-white/8">
                    <button onClick={onClose} className="rounded-xl px-4 py-2.5 text-sm font-medium text-slate-300 bg-white/5 hover:bg-white/10 ring-1 ring-white/10 transition">
                        Cancel
                    </button>
                    <button
                        onClick={handleRestore}
                        disabled={isRestoring || !parsed || selectedFolderIds.size === 0}
                        className="rounded-xl px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:opacity-90 disabled:opacity-40 transition"
                    >
                        {isRestoring ? 'Restoring…' : `Restore ${selectedCardCount} card${selectedCardCount === 1 ? '' : 's'}`}
                    </button>
                </div>
            </div>
        </div>
    );
}
